import { useCallback, useEffect, useRef, useState } from "react";
import { HttpError, request } from "../../../api/http";
import {
  IconClose,
  IconDownload,
  IconFile,
  IconFolder,
  IconImage,
  IconPdf,
  IconRotateLeft,
  IconRotateRight,
} from "../../../components/Icons";
import { useConfirm } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";
import {
  ResizeHandle,
  useResizableDrawer,
} from "../../../hooks/useResizableDrawer";
import { LeadStatus } from "../../../types/onboarding";
import BiometryActions from "./BiometryActions";
import CancellationRequestFolderActions from "./CancellationRequestFolderActions";
import ContractActions from "./ContractActions";
import IdentificationTypeSelector from "./IdentificationTypeSelector";
import ReportActions from "./ReportActions";

type FileEntry = {
  id: string | null;
  name: string;
  url: string;
  contentType: string | null;
  variant: string | null;
  deletable: boolean;
};

type Folder = {
  key: string;
  name: string;
  files: FileEntry[];
  subfolders?: Folder[];
};

type ApiResponse = { folders: Folder[] };

const IDENTIFICATION_SIDES = ["FRONT", "BACK", "SELFIE", "OTHER"] as const;

const IDENTIFICATION_SIDE_LABELS: Record<string, string> = {
  FRONT: "Frontal",
  BACK: "Reverso",
  SELFIE: "Selfie",
  OTHER: "Imagen adicional",
};

type Props = {
  leadId: number;
  leadUlid: string;
  customer: string;
  currentStatus: LeadStatus;
  onStatusChange: (next: LeadStatus) => void;
  onClose: () => void;
};

export default function FileExplorer({
  leadId,
  leadUlid,
  customer,
  currentStatus,
  onStatusChange,
  onClose,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Path of folder keys from root down to the open folder. Empty = top-level
  // grid. Single entry = first-level folder. Two entries = nested subfolder
  // (e.g. ["contracts", "old"]).
  const [openPath, setOpenPath] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileEntry | null>(null);
  const [uploading, setUploading] = useState(false);
  const [effectiveStatus, setEffectiveStatus] = useState<LeadStatus>(currentStatus);
  const { width: drawerWidth, startResize } = useResizableDrawer({
    initial: Math.min(900, Math.max(480, window.innerWidth - 80)),
    min: 480,
  });
  const [statusBusy, setStatusBusy] = useState<LeadStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const transitionTo = async (target: LeadStatus) => {
    if (statusBusy) return;
    if (target === "insurer_review") {
      const ok = await confirm({
        title: "Enviar a la aseguradora",
        message:
          "Se enviarán a la aseguradora los documentos del lead (DNI, contrato, datasheet) y el lead pasará a estado Insurer review. ¿Continuar?",
        confirmLabel: "Enviar",
        tone: "warning",
      });
      if (!ok) return;
    }
    setStatusBusy(target);
    setStatusError(null);
    try {
      if (target === "insurer_review") {
        // Mirror the legacy "Enviar a la aseguradora" button: this fires the
        // DocumentManager SendToInsurerCommandHandler, which gathers all the
        // lead documents (DNI front/back, contract PDF, datasheet CSV),
        // emails them to the carrier and transitions the lead to
        // insurer_review server-side.
        await request<{ sent: boolean }>(
          `/api/admin/operations/leads/${leadUlid}/send-to-insurer`,
          { method: "POST", token, body: {} },
        );
      } else {
        await request<{ status: string; changed: boolean }>(
          `/api/admin/operations/onboarding/${leadUlid}/status`,
          { method: "PATCH", body: { status: target }, token },
        );
      }
      setEffectiveStatus(target);
      onStatusChange(target);
    } catch (err) {
      setStatusError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cambiar",
      );
    } finally {
      setStatusBusy(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<ApiResponse>(
        `/api/admin/operations/leads/${leadUlid}/files`,
        { token },
      );
      setFolders(data.folders);
      if (data.folders.length === 1) {
        setOpenPath([data.folders[0].key]);
      }
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [leadUlid, token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (selectedFile) {
          setSelectedFile(null);
        } else if (openPath.length > 0) {
          setOpenPath((prev) => prev.slice(0, -1));
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [openPath, selectedFile, onClose]);

  const resolveFolder = (path: string[]): Folder | undefined => {
    if (path.length === 0) return undefined;
    let current: Folder | undefined = folders.find((f) => f.key === path[0]);
    for (let i = 1; current && i < path.length; i++) {
      current = current.subfolders?.find((s) => s.key === path[i]);
    }
    return current;
  };
  const openFolder = resolveFolder(openPath);
  const isRootFolder = openPath.length === 1;

  const uploadFiles = async (
    folderKey: string,
    files: FileList | File[],
    variant?: string,
  ) => {
    const list = Array.from(files);
    if (list.length === 0 || uploading) return;
    if (folderKey === "identification" && !variant) {
      setError("Selecciona el lado del documento antes de subir");
      return;
    }
    setUploading(true);
    setError(null);
    const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ?? "";
    const endpoint =
      folderKey === "identification"
        ? `/api/admin/operations/leads/${leadUlid}/identification`
        : `/api/admin/operations/leads/${leadUlid}/files`;
    try {
      for (const file of list) {
        const body = new FormData();
        body.append("file", file);
        if (folderKey === "identification" && variant) {
          body.append("variant", variant);
        }
        const response = await fetch(`${base}${endpoint}`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body,
        });
        if (!response.ok) {
          const payload = await response.text();
          throw new Error(`HTTP ${response.status} ${payload.slice(0, 120)}`);
        }
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload falló");
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (folderKey: string, file: FileEntry) => {
    if (!file.id || !file.deletable) return;
    const ok = await confirm({
      title: "Eliminar archivo",
      message: `¿Eliminar "${file.name}"? Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    setError(null);
    const path =
      folderKey === "identification"
        ? `/api/admin/operations/leads/${leadUlid}/identification/${encodeURIComponent(file.id)}`
        : `/api/admin/operations/leads/${leadUlid}/files/${file.id}`;
    try {
      await request<unknown>(path, { method: "DELETE", token });
      if (selectedFile?.url === file.url) setSelectedFile(null);
      await load();
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo borrar");
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 100,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          width: drawerWidth,
          height: "100vh",
          background: "white",
          display: "flex",
          position: "relative",
          flexDirection: "column",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
        }}
      >
        <ResizeHandle onMouseDown={startResize} />
        <header
          style={{
            padding: "0.85rem 1.25rem",
            borderBottom: "1px solid #e9ecef",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <Breadcrumbs
            leadId={leadId}
            customer={customer}
            path={openPath}
            folders={folders}
            onNavigate={(depth) => {
              setOpenPath((prev) => prev.slice(0, depth));
              setSelectedFile(null);
            }}
          />
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            {effectiveStatus === "ops_review" && (
              <button
                type="button"
                onClick={() => transitionTo("ready_to_send")}
                disabled={statusBusy !== null}
                style={statusActionStyle("primary", statusBusy !== null)}
              >
                {statusBusy === "ready_to_send"
                  ? "Marcando…"
                  : "Marcar como Ready to send"}
              </button>
            )}
            {effectiveStatus === "ready_to_send" && (
              <button
                type="button"
                onClick={() => transitionTo("insurer_review")}
                disabled={statusBusy !== null}
                style={statusActionStyle("success", statusBusy !== null)}
              >
                {statusBusy === "insurer_review"
                  ? "Enviando…"
                  : "Enviar a la aseguradora"}
              </button>
            )}
            {statusError && (
              <span style={{ color: "#b32424", fontSize: ".78rem" }}>
                {statusError}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              title="Cerrar"
              style={{
                background: "transparent",
                color: "#73879C",
                border: "1px solid #dee2e6",
                padding: "0.35rem",
                borderRadius: "0.4rem",
                cursor: "pointer",
                display: "inline-flex",
              }}
            >
              <IconClose />
            </button>
          </div>
        </header>

        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          <main
            style={{
              flex: selectedFile ? "0 0 360px" : 1,
              overflowY: "auto",
              padding: "1rem 1.25rem",
              borderRight: selectedFile ? "1px solid #e9ecef" : "none",
              transition: "flex-basis 0.2s ease",
            }}
          >
            {loading && (
              <div style={{ color: "#73879C", fontSize: ".85rem" }}>Cargando…</div>
            )}
            {error && (
              <div
                style={{
                  color: "#b32424",
                  background: "#fde2e2",
                  padding: "0.6rem 0.9rem",
                  borderRadius: "0.5rem",
                  fontSize: ".85rem",
                }}
              >
                {error}
              </div>
            )}
            {!loading && !error && (
              <>
                {!openFolder && (
                  <FolderGrid
                    folders={folders}
                    onOpen={(key) => {
                      setOpenPath([key]);
                      setSelectedFile(null);
                    }}
                  />
                )}
                {openFolder && (
                  <>
                    {isRootFolder && openFolder.key === "contracts" && (
                      <ContractActions
                        leadId={leadId}
                        leadUlid={leadUlid}
                        onAfterChange={load}
                      />
                    )}
                    {isRootFolder && openFolder.key === "reports" && (
                      <ReportActions
                        leadId={leadId}
                        leadUlid={leadUlid}
                        reportUrl={openFolder.files[0]?.url ?? null}
                        reportName={openFolder.files[0]?.name ?? null}
                        onAfterChange={load}
                      />
                    )}
                    {isRootFolder && openFolder.key === "cancellation_requests" && (
                      <CancellationRequestFolderActions
                        leadUlid={leadUlid}
                        onAfterChange={load}
                      />
                    )}
                    {isRootFolder && openFolder.key === "identification" && (
                      <>
                        <IdentificationTypeSelector
                          leadUlid={leadUlid}
                          onAfterChange={load}
                        />
                        <BiometryActions
                          leadUlid={leadUlid}
                          onAfterChange={load}
                        />
                      </>
                    )}
                    <FileGrid
                      folder={openFolder}
                      selectedUrl={selectedFile?.url ?? null}
                      onSelect={setSelectedFile}
                      onOpenSubfolder={(key) => {
                        setOpenPath((prev) => [...prev, key]);
                        setSelectedFile(null);
                      }}
                      onDelete={(file) => deleteFile(openFolder.key, file)}
                      onUpload={(files, variant) =>
                        uploadFiles(openFolder.key, files, variant)
                      }
                      uploading={uploading}
                    />
                  </>
                )}
              </>
            )}
          </main>

          {selectedFile && (
            <PreviewPane
              file={selectedFile}
              leadUlid={leadUlid}
              onClose={() => setSelectedFile(null)}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

function Breadcrumbs({
  leadId,
  customer,
  path,
  folders,
  onNavigate,
}: {
  leadId: number;
  customer: string;
  path: string[];
  folders: Folder[];
  onNavigate: (depth: number) => void;
}) {
  // Walk the path so we can show each segment's display name.
  const segments: Folder[] = [];
  let pool: Folder[] | undefined = folders;
  for (const key of path) {
    const match: Folder | undefined = pool?.find((f) => f.key === key);
    if (!match) break;
    segments.push(match);
    pool = match.subfolders;
  }

  return (
    <nav
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
        fontSize: ".9rem",
        flexWrap: "wrap",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => onNavigate(0)}
        disabled={path.length === 0}
        style={{
          background: "transparent",
          border: "none",
          color: path.length > 0 ? "#0069d9" : "#1f2933",
          fontWeight: 600,
          cursor: path.length > 0 ? "pointer" : "default",
          padding: 0,
        }}
      >
        Lead {leadId} · {customer}
      </button>
      {segments.map((folder, index) => {
        const isLast = index === segments.length - 1;
        return (
          <span key={folder.key} style={{ display: "inline-flex", gap: "0.4rem" }}>
            <span style={{ color: "#73879C" }}>/</span>
            {isLast ? (
              <span style={{ color: "#1f2933", fontWeight: 600 }}>
                {folder.name}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(index + 1)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#0069d9",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {folder.name}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function FolderGrid({
  folders,
  onOpen,
}: {
  folders: Folder[];
  onOpen: (key: string) => void;
}) {
  if (folders.length === 0) {
    return (
      <div style={{ color: "#73879C", fontSize: ".9rem", marginTop: "1rem" }}>
        No hay archivos para este lead.
      </div>
    );
  }
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: "1rem",
      }}
    >
      {folders.map((f) => (
        <button
          key={f.key}
          type="button"
          onDoubleClick={() => onOpen(f.key)}
          onClick={() => onOpen(f.key)}
          style={tileStyle}
        >
          <IconFolder
            style={{ width: "3.5rem", height: "3.5rem", fill: "#f1bf00" }}
          />
          <div style={tileLabelStyle}>{f.name}</div>
          <div style={{ fontSize: ".7rem", color: "#73879C" }}>
            {f.files.length} {f.files.length === 1 ? "archivo" : "archivos"}
          </div>
        </button>
      ))}
    </div>
  );
}

function FileGrid({
  folder,
  selectedUrl,
  onSelect,
  onOpenSubfolder,
  onDelete,
  onUpload,
  uploading,
}: {
  folder: Folder;
  selectedUrl: string | null;
  onSelect: (file: FileEntry) => void;
  onOpenSubfolder: (key: string) => void;
  onDelete: (file: FileEntry) => void;
  onUpload: (files: FileList | File[], variant?: string) => void;
  uploading: boolean;
}) {
  const subfolders = folder.subfolders ?? [];
  const isIdentification = folder.key === "identification";
  const writable = folder.key === "documents" || isIdentification;
  const [dragOver, setDragOver] = useState(false);
  const [identificationSide, setIdentificationSide] = useState<string>("FRONT");
  const dragDepth = useRef(0);

  const dragHandlers = writable
    ? {
        onDragEnter: (e: React.DragEvent) => {
          e.preventDefault();
          dragDepth.current += 1;
          setDragOver(true);
        },
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        },
        onDragLeave: () => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragOver(false);
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragOver(false);
          if (e.dataTransfer.files?.length) {
            onUpload(
              e.dataTransfer.files,
              isIdentification ? identificationSide : undefined,
            );
          }
        },
      }
    : {};

  const hasAnything = folder.files.length > 0 || subfolders.length > 0;
  const content = !hasAnything ? (
    <div style={{ color: "#73879C", fontSize: ".9rem", padding: "1rem 0" }}>
      {writable
        ? "Carpeta vacía. Arrastra archivos a cualquier punto de esta carpeta o usa el botón de arriba."
        : "Carpeta vacía."}
    </div>
  ) : (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: "1rem",
      }}
    >
      {subfolders.map((sub) => (
        <button
          key={sub.key}
          type="button"
          onClick={() => onOpenSubfolder(sub.key)}
          onDoubleClick={() => onOpenSubfolder(sub.key)}
          style={tileStyle}
        >
          <IconFolder
            style={{ width: "3.5rem", height: "3.5rem", fill: "#adb5bd" }}
          />
          <div style={tileLabelStyle}>{sub.name}</div>
          <div style={{ fontSize: ".7rem", color: "#73879C" }}>
            {sub.files.length}{" "}
            {sub.files.length === 1 ? "archivo" : "archivos"}
          </div>
        </button>
      ))}
      {folder.files.map((file) => (
        <FileTile
          key={file.url}
          file={file}
          selected={selectedUrl === file.url}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      ))}
    </div>
  );

  return (
    <div
      {...dragHandlers}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        minHeight: writable ? "calc(100vh - 200px)" : undefined,
        borderRadius: "0.6rem",
        border: writable
          ? dragOver
            ? "2px dashed #0069d9"
            : "2px dashed transparent"
          : undefined,
        background: dragOver ? "rgba(0,105,217,0.04)" : undefined,
        padding: writable ? "0.5rem" : undefined,
        transition: "background 0.1s ease, border-color 0.1s ease",
      }}
    >
      {writable && (
        <UploadZone
          onUpload={(files) =>
            onUpload(files, isIdentification ? identificationSide : undefined)
          }
          uploading={uploading}
          variantPicker={
            isIdentification
              ? {
                  value: identificationSide,
                  onChange: setIdentificationSide,
                  options: IDENTIFICATION_SIDES,
                  labels: IDENTIFICATION_SIDE_LABELS,
                }
              : undefined
          }
        />
      )}
      {content}
      {dragOver && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#0069d9",
            fontWeight: 600,
            fontSize: "1rem",
            background: "rgba(231, 241, 255, 0.85)",
            borderRadius: "0.6rem",
          }}
        >
          Suelta para subir
        </div>
      )}
    </div>
  );
}

function FileTile({
  file,
  selected,
  onSelect,
  onDelete,
}: {
  file: FileEntry;
  selected: boolean;
  onSelect: (file: FileEntry) => void;
  onDelete: (file: FileEntry) => void;
}) {
  return (
    <div
      onClick={() => onSelect(file)}
      style={{
        ...tileStyle,
        position: "relative",
        background: selected ? "#e7f1ff" : tileStyle.background,
        borderColor: selected ? "#0069d9" : tileStyle.borderColor,
      }}
    >
      {file.deletable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(file);
          }}
          title="Eliminar archivo"
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            width: "1.4rem",
            height: "1.4rem",
            padding: 0,
            borderRadius: "999px",
            background: "#fde2e2",
            color: "#b32424",
            border: "1px solid #f5c2c2",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: ".7rem",
          }}
        >
          <IconClose style={{ width: ".7rem", height: ".7rem" }} />
        </button>
      )}
      <FileIcon contentType={file.contentType} />
      <div style={tileLabelStyle} title={file.name}>
        {file.name}
      </div>
    </div>
  );
}

function UploadZone({
  onUpload,
  uploading,
  variantPicker,
}: {
  onUpload: (files: FileList | File[]) => void;
  uploading: boolean;
  variantPicker?: {
    value: string;
    onChange: (next: string) => void;
    options: readonly string[];
    labels?: Record<string, string>;
  };
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div
      style={{
        border: "1px dashed #dee2e6",
        background: "#f8f9fb",
        padding: "0.7rem",
        borderRadius: "0.5rem",
        cursor: uploading ? "wait" : "pointer",
        color: "#73879C",
        fontSize: ".82rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.45rem",
        alignItems: "center",
        transition: "background 0.1s ease",
      }}
    >
      {variantPicker && (
        <label
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
            fontSize: ".78rem",
            color: "#1f2933",
          }}
        >
          <span style={{ fontWeight: 600 }}>Lado:</span>
          <select
            value={variantPicker.value}
            onChange={(e) => variantPicker.onChange(e.target.value)}
            style={{
              padding: "0.25rem 0.45rem",
              borderRadius: "0.3rem",
              border: "1px solid #dee2e6",
              background: "white",
              fontSize: ".78rem",
            }}
          >
            {variantPicker.options.map((opt) => (
              <option key={opt} value={opt}>
                {variantPicker.labels?.[opt] ?? opt}
              </option>
            ))}
          </select>
        </label>
      )}
      <div
        onClick={() => inputRef.current?.click()}
        style={{ textAlign: "center", width: "100%" }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept="image/jpeg,image/png,image/tiff,application/pdf"
          onChange={(e) => {
            if (e.target.files?.length) onUpload(e.target.files);
            e.target.value = "";
          }}
        />
        {uploading
          ? "Subiendo…"
          : variantPicker
            ? `Click para subir el documento (${
                variantPicker.labels?.[variantPicker.value] ?? variantPicker.value
              }), o arrastra a la carpeta`
            : "Click para seleccionar archivos, o arrastra a cualquier punto de la carpeta"}
      </div>
    </div>
  );
}

function FileIcon({ contentType }: { contentType: string | null }) {
  const ct = (contentType ?? "").toLowerCase();
  if (ct.startsWith("image/")) {
    return (
      <IconImage
        style={{ width: "3.5rem", height: "3.5rem", fill: "#6f42c1" }}
      />
    );
  }
  if (ct.includes("pdf")) {
    return (
      <IconPdf style={{ width: "3.5rem", height: "3.5rem", fill: "#b32424" }} />
    );
  }
  return (
    <IconFile style={{ width: "3.5rem", height: "3.5rem", fill: "#73879C" }} />
  );
}

function PreviewPane({
  file,
  leadUlid,
  onClose,
}: {
  file: FileEntry;
  leadUlid: string;
  onClose: () => void;
}) {
  const ct = (file.contentType ?? "").toLowerCase();
  const isImage = ct.startsWith("image/");
  const isPdf = ct.includes("pdf");
  const isCsv = ct === "text/csv" || file.name.toLowerCase().endsWith(".csv");

  // Visual rotation only — does not alter the binary in GCS. Reset whenever
  // the previewed file changes so each doc starts at its native orientation.
  const [rotation, setRotation] = useState(0);
  useEffect(() => setRotation(0), [file.url]);
  const rotate = (delta: number) =>
    setRotation((prev) => (((prev + delta) % 360) + 360) % 360);
  const isSideways = rotation === 90 || rotation === 270;

  return (
    <aside
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        background: "#1a1a2e",
      }}
    >
      <div
        style={{
          padding: "0.7rem 1rem",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <span
          style={{
            fontSize: ".85rem",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={file.name}
        >
          {file.name}
        </span>
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          download
          title="Descargar / abrir"
          style={{
            background: "white",
            color: "#1f2933",
            padding: "0.35rem 0.65rem",
            borderRadius: "0.4rem",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
            fontSize: ".8rem",
            fontWeight: 500,
          }}
        >
          <IconDownload /> Abrir
        </a>
        {isImage && (
          <>
            <button
              type="button"
              onClick={() => rotate(-90)}
              title="Rotar 90° a la izquierda"
              style={iconButtonStyle}
            >
              <IconRotateLeft />
            </button>
            <button
              type="button"
              onClick={() => rotate(90)}
              title="Rotar 90° a la derecha"
              style={iconButtonStyle}
            >
              <IconRotateRight />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Cerrar vista previa"
          style={{
            background: "transparent",
            color: "white",
            border: "1px solid rgba(255,255,255,0.3)",
            padding: "0.35rem",
            borderRadius: "0.4rem",
            cursor: "pointer",
            display: "inline-flex",
          }}
        >
          <IconClose />
        </button>
      </div>
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isImage ? "1rem" : 0,
        }}
      >
        {isImage && (
          <img
            src={file.url}
            alt={file.name}
            style={{
              // Swap max-width / max-height when sideways so the rotated
              // image still fits inside the preview pane.
              maxWidth: isSideways ? "100%" : "100%",
              maxHeight: isSideways ? "100%" : "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: "0.4rem",
              boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
              transform: `rotate(${rotation}deg)`,
              transition: "transform 0.15s ease",
            }}
          />
        )}
        {isPdf && (
          <iframe
            title={file.name}
            src={file.url}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              background: "white",
            }}
          />
        )}
        {isCsv && <CsvPreview leadUlid={leadUlid} />}
        {!isImage && !isPdf && !isCsv && (
          <div style={{ color: "#adb5bd", fontSize: ".9rem", padding: "2rem" }}>
            Vista previa no disponible para {file.contentType ?? "este tipo"}.
            Usa "Abrir" para descargar.
          </div>
        )}
      </div>
    </aside>
  );
}

function CsvPreview({ leadUlid }: { leadUlid: string }) {
  const { token } = useAuth();
  const [data, setData] = useState<{
    hasReport: boolean;
    lines: string[][];
    maxFields: number;
    truncated?: boolean;
    rowsTotal?: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    request<typeof data & object>(
      `/api/admin/operations/leads/${leadUlid}/csv-preview`,
      { token },
    )
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof HttpError
            ? `HTTP ${err.status}`
            : "No se pudo cargar la vista previa",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadUlid, token]);

  if (loading) {
    return (
      <div style={{ color: "#adb5bd", padding: "2rem" }}>Cargando CSV…</div>
    );
  }
  if (error) {
    return <div style={{ color: "#f87171", padding: "2rem" }}>{error}</div>;
  }
  if (!data || !data.hasReport || data.lines.length === 0) {
    return (
      <div style={{ color: "#adb5bd", padding: "2rem" }}>CSV vacío.</div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        background: "white",
        padding: "0.75rem",
      }}
    >
      {data.truncated && (
        <div
          style={{
            background: "#fff3cd",
            color: "#856404",
            padding: "0.45rem 0.7rem",
            borderRadius: "0.4rem",
            fontSize: ".75rem",
            marginBottom: "0.5rem",
          }}
        >
          Mostrando primeras {data.lines.length} filas de {data.rowsTotal}.
        </div>
      )}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: ".78rem",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        <tbody>
          {data.lines.map((row, ri) => (
            <tr
              key={ri}
              style={{
                background: ri === 0 ? "#f8f9fb" : ri % 2 === 0 ? "white" : "#fafbfc",
              }}
            >
              {Array.from({ length: data.maxFields }).map((_, ci) => (
                <td
                  key={ci}
                  style={{
                    border: "1px solid #e9ecef",
                    padding: "0.35rem 0.5rem",
                    verticalAlign: "top",
                    fontWeight: ri === 0 ? 600 : 400,
                    color: ri === 0 ? "#2A3F54" : "#1f2933",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxWidth: 320,
                  }}
                >
                  {row[ci] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function statusActionStyle(
  tone: "primary" | "success",
  disabled: boolean,
): React.CSSProperties {
  const palette =
    tone === "success"
      ? { bg: "#1a7f37", border: "#1a7f37" }
      : { bg: "#0069d9", border: "#0069d9" };
  return {
    padding: "0.4rem 0.85rem",
    borderRadius: "0.4rem",
    background: palette.bg,
    color: "white",
    border: `1px solid ${palette.border}`,
    fontSize: ".78rem",
    fontWeight: 600,
    cursor: disabled ? "wait" : "pointer",
    opacity: disabled ? 0.7 : 1,
    whiteSpace: "nowrap",
  };
}

const iconButtonStyle: React.CSSProperties = {
  background: "transparent",
  color: "white",
  border: "1px solid rgba(255,255,255,0.3)",
  padding: "0.35rem",
  borderRadius: "0.4rem",
  cursor: "pointer",
  display: "inline-flex",
};

const tileStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.45rem",
  padding: "0.9rem 0.5rem",
  borderRadius: "0.6rem",
  background: "white",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
  textAlign: "center",
  transition: "background 0.1s ease, border-color 0.1s ease",
};

const tileLabelStyle: React.CSSProperties = {
  fontSize: ".78rem",
  fontWeight: 500,
  width: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  wordBreak: "break-word",
  minHeight: "2.4em",
};
